import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, CalendarClock } from "lucide-react";
import { CLINIC } from "../lib/content";
import { CONDITION_GROUPS } from "../lib/conditions";

function conditionSlug(name) {
  return name.toLowerCase().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");
}

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
    <span
      className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden ring-1 ring-border shrink-0 bg-white"
      aria-label="Mom and More Clinic"
    >
      <img
        src={CLINIC.logo}
        alt="Mom & More Clinic"
        className="absolute w-[145%] h-[145%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-cover"
      />
    </span>
    <span className="hidden sm:flex flex-col leading-none">
      <span className="font-serif text-xl text-primary tracking-tight">Mom &amp; More</span>
      <span className="overline text-[0.58rem] text-muted-foreground mt-1.5">Clinic · Bengaluru</span>
    </span>
  </Link>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [mExpanded, setMExpanded] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mega) return;
    const onEsc = (e) => e.key === "Escape" && setMega(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [mega]);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || mega ? "bg-background/90 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1 rounded-full p-1 border border-white/60 bg-white/45 backdrop-blur-xl" style={{ boxShadow: "0 8px 30px -12px rgba(94,39,65,0.15), inset 0 1px 0 rgba(255,255,255,0.6)" }}>
          <button
            data-testid="nav-conditions-btn"
            onClick={() => setMega((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[0.86rem] font-sans font-extrabold uppercase tracking-tight transition-colors ${
              mega ? "bg-accent text-accent-foreground" : "text-primary hover:bg-white/60"
            }`}
          >
            Conditions we treat
            <ChevronDown size={14} className={`transition-transform duration-300 ${mega ? "rotate-180" : ""}`} />
          </button>
          <a href="/#about" data-testid="nav-about" className="px-5 py-2.5 rounded-full text-[0.86rem] font-sans font-extrabold uppercase tracking-tight text-primary hover:bg-white/60 transition-colors">
            About Dr. Yasmin
          </a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="/#booking"
            data-testid="header-book-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-accent text-accent-foreground px-5 sm:px-6 py-2.5 text-[0.86rem] font-sans font-extrabold uppercase tracking-tight hover:-translate-y-0.5 transition-transform duration-300"
            style={{ boxShadow: "0 12px 28px -10px rgba(228,71,122,0.55)" }}
          >
            Book Appointment
          </a>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-primary p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Desktop mega-menu */}
      <AnimatePresence>
        {mega && (
          <motion.div
            data-testid="desktop-mega-menu"
            data-lenis-prevent
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute left-0 right-0 top-full bg-background border-b border-border shadow-[0_40px_60px_-30px_rgba(228,71,122,0.25)] max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain"
          >
            <div className="max-w-7xl mx-auto px-8 py-10">
              <div className="flex items-start justify-between mb-9">
                <div>
                  <p className="overline text-accent">Care areas</p>
                  <p className="font-serif text-primary text-3xl xl:text-4xl mt-3 leading-tight max-w-xl">
                    Whatever brought you here, <span className="text-accent italic">we&rsquo;ve got you</span>.
                  </p>
                </div>
                <button onClick={() => setMega(false)} aria-label="Close menu" className="text-muted-foreground hover:text-accent transition-colors p-2 -m-2">
                  <X size={20} />
                </button>
              </div>

              <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-10">
                {CONDITION_GROUPS.map((g) => (
                  <div key={g.title}>
                    <p className="font-sans text-primary text-[0.95rem] font-extrabold tracking-tight uppercase">{g.title}</p>
                    <p className="text-xs text-muted-foreground mt-1.5 mb-4 leading-relaxed">{g.blurb}</p>
                    <ul className="space-y-2">
                      {g.items.map((i) => (
                        <li key={i}>
                          <Link
                            to={`/care/${conditionSlug(i)}`}
                            data-testid={`nav-cond-${conditionSlug(i)}`}
                            onClick={() => setMega(false)}
                            className="text-left text-sm text-foreground/85 font-medium hover:text-accent hover:translate-x-0.5 transition-[color,transform] duration-200 group inline-flex items-center gap-1.5"
                          >
                            {i}
                            <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-border flex items-center justify-between gap-6 flex-wrap">
                <p className="text-sm text-muted-foreground max-w-lg">
                  Not sure which one applies to you? Book a consultation and Dr. Yasmin will guide you.
                </p>
                <a
                  href="/#booking"
                  onClick={() => setMega(false)}
                  className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 text-sm font-bold hover:-translate-y-0.5 transition-transform"
                  style={{ boxShadow: "0 12px 28px -10px rgba(228,71,122,0.55)" }}
                >
                  <CalendarClock size={15} /> Book a consultation
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-background border-t border-border overflow-hidden"
          >
            <div data-testid="mobile-menu-panel" data-lenis-prevent className="px-5 py-4 max-h-[80vh] overflow-y-auto overscroll-contain">
              <a
                href="/#booking"
                data-testid="mobile-book-link"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-2xl bg-accent text-accent-foreground px-4 py-3 mb-4"
              >
                <span className="font-sans font-extrabold uppercase tracking-tight text-[0.78rem]">Book an appointment</span>
                <CalendarClock size={16} />
              </a>
              <a
                href="/#about"
                data-testid="mobile-about-link"
                onClick={() => setOpen(false)}
                className="block py-3 font-sans font-extrabold uppercase tracking-tight text-[0.78rem] text-primary border-b border-border"
              >
                About Dr. Yasmin
              </a>
              <p className="mt-5 mb-2 overline text-accent">Conditions we treat</p>
              <ul className="divide-y divide-border">
                {CONDITION_GROUPS.map((g, idx) => {
                  const isOpen = mExpanded === idx;
                  return (
                    <li key={g.title}>
                      <button
                        onClick={() => setMExpanded(isOpen ? null : idx)}
                        className="w-full flex items-center justify-between py-3.5 text-left"
                      >
                        <span className="font-sans font-extrabold uppercase tracking-tight text-primary text-sm">{g.title}</span>
                        <ChevronDown size={16} className={`text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <ul className="pb-4 space-y-2.5">
                          {g.items.map((i) => (
                            <li key={i}>
                              <Link
                                to={`/care/${conditionSlug(i)}`}
                                data-testid={`mnav-cond-${conditionSlug(i)}`}
                                onClick={() => setOpen(false)}
                                className="text-left text-sm text-foreground/85 font-medium py-1 flex items-center gap-1.5"
                              >
                                {i} <ArrowUpRight size={11} className="text-accent" />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-muted-foreground mt-5">{CLINIC.phone}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
